import React from 'react'
import CloseIcon from '@mui/icons-material/Close'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { Box, Button, Divider, Drawer, IconButton, Skeleton, Stack, Typography } from '@mui/material'
import TutorialListItem from './TutorialListItem'
import type { TrainingVideo } from '../../types/Training/TrainingVideo'

type TutorialsDrawerProps = {
	open: boolean
	onClose: () => void
	// pageLabel: string
	tutorials: TrainingVideo[]
	isLoading: boolean
	errorMessage: string | null
	onRetry: () => Promise<void>
	onSelectTutorial: (tutorial: TrainingVideo) => void
}

const TutorialsDrawer = ({
	open,
	onClose,
	// pageLabel,
	tutorials,
	isLoading,
	errorMessage,
	onRetry,
	onSelectTutorial,
}: TutorialsDrawerProps) => {
	return (
		<Drawer
			anchor='right'
			open={open}
			onClose={onClose}
			PaperProps={{
				sx: {
					top: '64px',
					height: 'calc(100% - 64px)',
				},
			}}
		>
			<Stack sx={{ width: { xs: '88vw', sm: 380, md: 400 }, height: '100%' }}>
				<Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ px: 2, py: 1 }}>
					<Box>
						<Typography variant='h6'>Help for this page</Typography>
					</Box>
					<IconButton aria-label='Close help drawer' onClick={onClose}>
						<CloseIcon />
					</IconButton>
				</Stack>
				<Divider />

				<Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
					{isLoading && (
						<Stack spacing={1.5}>
							{[0, 1, 2, 3].map((item) => (
								<Stack
									key={item}
									direction='row'
									spacing={1.25}
									sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1 }}
								>
									<Skeleton variant='rectangular' width={96} height={60} sx={{ borderRadius: 1 }} />
									<Stack spacing={0.75} flex={1}>
										<Skeleton variant='text' width='80%' />
										<Skeleton variant='text' width='95%' />
										<Skeleton variant='text' width='65%' />
									</Stack>
								</Stack>
							))}
						</Stack>
					)}

					{!isLoading && errorMessage && (
						<Stack spacing={1.25} alignItems='flex-start' sx={{ py: 2 }}>
							<Stack direction='row' spacing={1} alignItems='center'>
								<ErrorOutlineIcon color='error' />
								<Typography variant='body1'>{errorMessage}</Typography>
							</Stack>
							<Button variant='outlined' onClick={() => void onRetry()}>
								Retry
							</Button>
						</Stack>
					)}

					{!isLoading && !errorMessage && tutorials.length === 0 && (
						<Box sx={{ py: 3 }}>
							<Typography variant='body1'>No tutorials available for this page.</Typography>
							<Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
								Check again later or contact your admin for training content.
							</Typography>
						</Box>
					)}

					{!isLoading && !errorMessage && tutorials.length > 0 && (
						<Stack spacing={1.25}>
							{tutorials.map((tutorial) => (
								<TutorialListItem key={tutorial.id} tutorial={tutorial} onClick={onSelectTutorial} />
							))}
						</Stack>
					)}
				</Box>
			</Stack>
		</Drawer>
	)
}

export default TutorialsDrawer
